import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Divider,
  Alert,
  Paper,
  Avatar,
  TextField,
  Fab,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCartOutlined,
  ArrowBack,
  LocalShipping,
  Payment,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import {
  selectCartItems,
  selectCartTotal,
  selectCartQuantity,
  updateQuantity,
  removeFromCart,
  clearCart,
} from '../../store/slices/cartSlice';
import { surface, brand, ink, line, status } from '../../theme/tokens';
import Seo from '../../components/Common/Seo';

// Convert signed URLs to public URLs
const getPublicImageUrl = (url) => {
  // Handle null or undefined
  if (!url) {
    return '/placeholder-product.svg';
  }
  
  // Convert URL object to string if needed
  let urlString;
  if (typeof url === 'string') {
    urlString = url;
  } else if (url instanceof URL) {
    urlString = url.toString();
  } else if (typeof url === 'object' && url.href) {
    urlString = url.href;
  } else {
    console.warn('getPublicImageUrl received unsupported value:', url, typeof url);
    return '/placeholder-product.svg';
  }
  
  // Extract the key from the signed URL
  const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
  const region = 'ap-south-1';
  
  // Check if it's already a signed URL from S3
  if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
    // Extract the file path from the signed URL
    const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
    const filePath = urlParts ? urlParts.split('?')[0] : '';
    
    // Return public URL (since files are in public/ folder)
    return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
  }
  
  return urlString;
};

const CartPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const cartQuantity = useSelector(selectCartQuantity);
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  const [isLoading, setIsLoading] = useState(false);

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) {
      dispatch(removeFromCart(productId));
    } else {
      dispatch(updateQuantity({ productId, quantity: newQuantity }));
    }
  };

  const handleRemoveItem = (productId) => {
    dispatch(removeFromCart(productId));
  };

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleCheckout = () => {
    if (isAuthenticated) {
      navigate('/checkout');
    } else {
      navigate('/register', { state: { from: '/cart', action: 'checkout' } });
    }
  };

  const handleLogin = () => {
    navigate('/login', { state: { from: '/cart', action: 'checkout' } });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
          <ShoppingCartOutlined 
            sx={{ 
              fontSize: { xs: 80, md: 120 }, 
              color: ink.muted, 
              mb: { xs: 2, md: 3 } 
            }} 
          />
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2.125rem' },
              fontWeight: 700,
              letterSpacing: '-0.015em',
              color: ink.primary,
              mb: { xs: 1, md: 2 }
            }}
          >
            Your Cart is Empty
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              color: ink.secondary,
              px: { xs: 2, md: 0 }
            }}
          >
            Looks like you haven't added any items to your cart yet.
          </Typography>
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            startIcon={<CartIcon />}
            onClick={() => navigate('/products')}
            sx={{ 
              px: { xs: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Continue Shopping
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Seo
        title="Your Cart"
        description="Review the organic produce in your basket before you check out."
        path="/cart"
        noIndex
      />

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: { xs: 3, md: 4 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flex: 1,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-start' }
        }}>
          <IconButton 
            onClick={() => navigate('/products')}
            sx={{ 
              mr: { xs: 1, md: 2 },
              p: { xs: 1, md: 1.5 }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              fontWeight: 700,
              letterSpacing: '-0.015em',
              color: ink.primary,
              flex: 1
            }}
          >
            Shopping Cart
          </Typography>
          <Chip 
            label={`${cartQuantity} items`}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{ 
              display: { xs: 'flex', sm: 'none' },
              fontSize: { xs: '0.75rem' }
            }}
          />
        </Box>
        <Box sx={{ 
          display: { xs: 'none', sm: 'flex' }, 
          alignItems: 'center', 
          gap: 2 
        }}>
          <Chip 
            label={`${cartQuantity} items`}
            color="primary"
            size="medium"
          />
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleClearCart}
            sx={{ minWidth: { xs: 'auto', md: 100 } }}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Cart Items */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: { xs: 1.5, md: 2 } 
          }}>
            {cartItems.map((item) => (
              <Card 
                key={item.id}
                elevation={0}
                sx={{ 
                  backgroundColor: surface.card,
                  border: `1px solid ${line.subtle}`,
                  borderRadius: 3,
                  '&:hover': { 
                    borderColor: line.default,
                    boxShadow: { xs: 'none', md: '0 10px 24px rgba(75,58,55,0.10)' },
                    transform: { xs: 'none', md: 'translateY(-2px)' }
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <CardContent sx={{ 
                  p: { xs: 2, md: 3 },
                  '&:last-child': { pb: { xs: 2, md: 3 } }
                }}>
                  <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
                    {/* Product Image */}
                    <Grid item xs={3} sm={2} md={2}>
                      <Avatar
                        src={getPublicImageUrl(item.image)}
                        alt={item.name}
                        variant="rounded"
                        sx={{ 
                          width: { xs: 60, sm: 80, md: 100 },
                          height: { xs: 60, sm: 80, md: 100 },
                          borderRadius: 2,
                          backgroundColor: surface.sunken,
                          border: `1px solid ${line.subtle}`
                        }}
                        onError={(e) => {
                          console.error('🖼️ Cart image failed to load:', {
                            originalUrl: item.image,
                            processedUrl: getPublicImageUrl(item.image),
                            itemName: item.name
                          });
                          e.target.src = '/placeholder-product.svg';
                        }}
                        onLoad={() => {
                          console.log('✅ Cart image loaded:', {
                            originalUrl: item.image,
                            processedUrl: getPublicImageUrl(item.image),
                            itemName: item.name
                          });
                        }}
                      />
                    </Grid>

                    {/* Product Details */}
                    <Grid item xs={9} sm={10} md={4}>
                      <Typography 
                        variant="h6" 
                        component="h3"
                        sx={{ 
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          mb: { xs: 0.5, md: 1 },
                          lineHeight: 1.3,
                          fontWeight: 700,
                          letterSpacing: '-0.01em',
                          color: ink.primary
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: { xs: '0.8rem', md: '0.875rem' },
                          color: ink.muted,
                          mb: { xs: 1, md: 0.5 }
                        }}
                      >
                        {formatPrice(item.price)} per {item.unit || 'unit'}
                      </Typography>
                      {isMobile && (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: brand.mustardInk
                          }}
                        >
                          {formatPrice(item.totalPrice)}
                        </Typography>
                      )}
                    </Grid>

                    {/* Quantity Controls */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: { xs: 'space-between', md: 'center' },
                        gap: { xs: 1, md: 2 }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            display: { xs: 'block', md: 'none' },
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          Quantity:
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          border: `1px solid ${line.default}`,
                          borderRadius: 2,
                          p: 0.5
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            value={item.quantity}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              handleQuantityChange(item.id, value);
                            }}
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            sx={{
                              width: 40,
                              '& input': {
                                textAlign: 'center',
                                fontSize: '0.875rem',
                                p: 0.5
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            disabled={item.quantity >= (item.maxQuantity || 10)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Price and Actions */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                        gap: { xs: 1, md: 2 }
                      }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            fontSize: { xs: '1.1rem', md: '1.25rem' },
                            color: brand.mustardInk,
                            display: { xs: 'none', md: 'block' }
                          }}
                        >
                          {formatPrice(item.totalPrice)}
                        </Typography>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveItem(item.id)}
                          size={isMobile ? "small" : "medium"}
                        >
                          <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Mobile Clear All Button */}
          {isMobile && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearCart}
                fullWidth
                sx={{ 
                  py: 1.5,
                  fontSize: '0.9rem'
                }}
              >
                Clear All Items
              </Button>
            </Box>
          )}
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} lg={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2.5, md: 3 },
              backgroundColor: surface.card,
              border: `1px solid ${line.default}`,
              borderRadius: 3,
              position: { lg: 'sticky' },
              top: { lg: 20 }
            }}
          >
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                fontWeight: 700,
                letterSpacing: '-0.015em',
                color: ink.primary,
                mb: { xs: 2, md: 3 }
              }}
            >
              Order Summary
            </Typography>

            <Box sx={{ mb: { xs: 2, md: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mb: 1 
              }}>
                <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Subtotal ({cartQuantity} items)
                </Typography>
                <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  {formatPrice(cartTotal)}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mb: 1 
              }}>
                <Typography variant="body2" sx={{ color: ink.secondary }}>
                  Delivery Fee
                </Typography>
                <Typography variant="body2" sx={{ color: status.success, fontWeight: 700 }}>
                  FREE
                </Typography>
              </Box>
              <Divider sx={{ my: { xs: 1.5, md: 2 }, borderColor: line.subtle }} />
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mb: { xs: 2, md: 3 }
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    fontWeight: 700,
                    color: ink.primary
                  }}
                >
                  Total
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    fontWeight: 700,
                    color: brand.mustardInk
                  }}
                >
                  {formatPrice(cartTotal)}
                </Typography>
              </Box>
            </Box>

            {isAuthenticated ? (
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<Payment />}
                onClick={handleCheckout}
                disabled={isLoading}
                sx={{ 
                  py: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  mb: { xs: 2, md: 3 }
                }}
              >
                Proceed to Checkout
              </Button>
            ) : (
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<Payment />}
                  onClick={handleCheckout}
                  disabled={isLoading}
                  sx={{ 
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    mb: 1
                  }}
                >
                  Register to Checkout
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleLogin}
                  disabled={isLoading}
                  sx={{ 
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}
                >
                  Already have an account? Login
                </Button>
              </Box>
            )}

            <Alert 
              severity="info" 
              icon={<LocalShipping />}
              sx={{ 
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.8rem', md: '0.875rem' }
                }
              }}
            >
              Free delivery on all orders above ₹500
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* Mobile Checkout FAB */}
      {isMobile && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleCheckout}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            left: 20,
            borderRadius: 28,
            height: 56,
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          <Payment sx={{ mr: 1 }} />
          {isAuthenticated ? `Checkout • ${formatPrice(cartTotal)}` : `Register to Checkout • ${formatPrice(cartTotal)}`}
        </Fab>
      )}
    </Container>
  );
};

export default CartPage;